import cloneDeep from 'lodash/cloneDeep';

import { fillVariables } from '@/services/templates.js';

export function stateToQueryProps({ state, getters }, hideRoomsData = false, debugFilterMode = false) {
  const { priceRange } = state.filters;
  const priceRangeFilters = {
    min_price: priceRange.value.from === getters.currentPriceMethodBoundaries[0] ? null : state.filters.priceRange.value.from,
    max_price: priceRange.value.to === getters.currentPriceMethodBoundaries[1] ? null : state.filters.priceRange.value.to,
  };

  if (debugFilterMode) {
    return {
      ...state.boundaries,
      room_tag: state.eventTypeSlug,
      form_timestamp: window._VUE_SEARCH_CONFIG.queryParams.form_timestamp,
      getAllRoomsPositions: true,
      items_per_page: state.debugFilterModeMaxRooms,
      iso_country_code: state.l10n.ISOCountryCode,
      neighbourhood: state.filters.location.value,
    };
  }

  const obj = {
    // lat/long boundaries
    ...state.boundaries,
    form_timestamp: window._VUE_SEARCH_CONFIG.queryParams.form_timestamp,

    getAllRoomsPositions: true,
    hideRoomsData,
    items_per_page: state.perPage,
    map_zoom: state.mapZoom,
    people: state.filters.people.value || undefined,

    allowed_events: state.filters.allowedEvents.value,
    catering_options: state.filters.catering.value,
    venueFeatures: state.filters.venueFeatures.value,
    room_features: [
      ...state.filters.roomFeatures.value,
    ],

    date: state.filters.date.value,

    time_from: state.filters.timeFrom.value,
    time_to: state.filters.timeTo.value,

    room_layout: state.filters.layout.value,

    ...priceRangeFilters,
    price_range: state.filters.customPriceRange.value,
    price_method: getters.hasPricingMethodFilter ? state.filters.priceMethod.value : null,
    neighbourhood: state.filters.location.value,
    page: state.page,
    room_tag: state.eventTypeSlug,
    room_tags: state.filters.roomTags?.value,

    supervenues_only: state.filters.superVenuesOnly.value,
    flexible_cancellation_only: state.filters.flexibleCancellation.value,
    venue_parts: state.filters.venueParts.value,
    venue_types: state.filters.venueTypes.value,
    accessibility_features: getters.appliedAccessibilityFilters,
    no_age_restriction: state.filters.noAgeRestriction.value,

    iso_country_code: state.l10n.ISOCountryCode,
    view: state.mobileView,
    trigger: state.trigger,
  };

  if (state.utmSource) {
    obj.utm_source = state.utmSource;
  }
  if (state.utmMedium) {
    obj.utm_medium = state.utmMedium;
  }
  if (state.utmCampaign) {
    obj.utm_campaign = state.utmCampaign;
  }
  if (state.utmContent) {
    obj.utm_content = state.utmContent;
  }
  if (state.utmTerm) {
    obj.utm_term = state.utmTerm;
  }
  if (state.triggerSection) {
    obj.trigger_section = state.triggerSection;
  }
  if (state.highlightedRoomId) {
    obj.highlighted_room_id = state.highlightedRoomId;
  }

  return obj;
}

export function stateToQuery({ state, getters }, hideRoomsData = false, debugFilterMode = false) {
  return $.param(stateToQueryProps({ state, getters }, hideRoomsData, debugFilterMode));
}

export const LICENCE_FEATURES = ['1', '2', '3', '4'];

export function filterLicences(featuresObject, positive) {
  return Object.keys(featuresObject)
    .filter(key => featuresObject[key] || featuresObject[key] === '')
    .filter(key => (LICENCE_FEATURES.indexOf(key) >= 0) === positive);
}

export function featuresListToMap(list) {
  return list.reduce((acc, item) => {
    acc[item] = true;
    return acc;
  }, {});
}

export function filterToCountry(items, ISOCountryCode) {
  return items.filter(item => !item.limitToCountry || item.limitToCountry === ISOCountryCode);
}

export function generateSearchPath(routeTemplate, countrySubdir, eventTypeSlug) {
  return fillVariables(routeTemplate, {
    subdir: countrySubdir,
    roomTag: eventTypeSlug,
  });
}

export function updateURLParams({
  query,
  searchPath,
  replaceState,
  historyApiState,
} = {}) {
  // ignore url changes if browser doesn't support History API
  if (!window.history.pushState) {
    return;
  }

  const newUrl = `${searchPath}?${query}`;

  const obj = {
    path: newUrl,
    ...historyApiState,
  };
  if (replaceState) {
    window.history.replaceState(cloneDeep(obj), '', newUrl);
  } else {
    window.history.pushState(cloneDeep(obj), '', newUrl);
  }
}

export function isMobile() {
  return window.innerWidth <= 991;
}

export function isMapViewForcedByUrl() {
  return window.location.search.indexOf('view=map') > -1;
}

export function getUrlParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

export function waitForGoogleMaps(callback) {
  if (window.googleMapsSearchLoaded) {
    callback();
    return;
  }

  window.googleMapsQueue ??= [];
  window.googleMapsQueue.push(callback);
}

const roomDataCache = {};
const roomDataCacheCallbacksQueue = {};

export function getRoomData({ roomId, roomTagSlug }, callback) {
  const cacheKey = roomId + roomTagSlug;
  if (roomDataCache[cacheKey]) {
    callback?.(null, roomDataCache[cacheKey]);
    return;
  }

  roomDataCacheCallbacksQueue[cacheKey] ??= [];
  roomDataCacheCallbacksQueue[cacheKey].push(callback);

  // If queue already had elements, do not request twice
  if (roomDataCacheCallbacksQueue[cacheKey]?.length >= 2) {
    return;
  }

  $.getJSON(`${window._VUE_SEARCH_CONFIG.searchUrl}/getMapRoomData/${roomId}?roomTagSlug=${roomTagSlug}`, (data) => {
    roomDataCache[cacheKey] = data;

    roomDataCacheCallbacksQueue[cacheKey]?.forEach(cb => cb?.(null, data));
    roomDataCacheCallbacksQueue[cacheKey] = [];
  });
}

export function scrollTop() {
  App.es6.shared.raf(() => {
    $(window).scrollTop(0);
    $('html,body').scrollTop(0);
  });
}

export function isFutureDate(date) {
  if (!date) {
    return false;
  }
  return window.dayjs(date, 'DD/MM/YYYY').isAfter(window.dayjs());
}

export function shrinkBoundariesToFitPins(pins, boundary) {
  // when zero or 1 pin provided it's impossible to build shrinked map
  if (pins.length <= 2) {
    return boundary;
  }

  // start with whatever first point
  const newBounds = {
    latitude_from: pins[0].latitude,
    latitude_to: pins[0].latitude,
    longitude_from: pins[0].longitude,
    longitude_to: pins[0].longitude,
  };

  pins.forEach(pin => {
    newBounds.latitude_from = Math.min(newBounds.latitude_from, pin.latitude);
    newBounds.latitude_to = Math.max(newBounds.latitude_to, pin.latitude);
    newBounds.longitude_from = Math.min(newBounds.longitude_from, pin.longitude);
    newBounds.longitude_to = Math.max(newBounds.longitude_to, pin.longitude);
  });

  return newBounds;
}

export function enlargeBoundaries(boundary, times = 3) {
  const centerLat = (boundary.latitude_to + boundary.latitude_from) / 2;
  const centerLong = (boundary.longitude_to + boundary.longitude_from) / 2;

  const latDiff = (boundary.latitude_to - boundary.latitude_from) / 2;
  const longDiff = (boundary.longitude_to - boundary.longitude_from) / 2;

  return {
    latitude_from: centerLat - latDiff * times,
    latitude_to: centerLat + latDiff * times,
    longitude_from: centerLong - longDiff * times,
    longitude_to: centerLong + longDiff * times,
  };
}
